import {connectToStores} from 'epic-alt-utils';
import {Container, HttpErrorScreen, LoadScreen} from 'epic-fortnite-react';
import LoginActions from 'epic-fortnite-react/actions/LoginActions';
import LocalizationStore from 'epic-fortnite-react/stores/LocalizationStore';
import {islandUrlStripTitle} from 'epic-fortnite-react/utils/FortniteUtils';
import {getInitialCmsPageSharedValues, useCmsData, PageData} from 'epic-fortnite-shared-values';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React, {useEffect} from 'react';
import Helmet from 'react-helmet';
import {withRouter} from 'react-router';
import styled from 'styled-components';

import FeaturedIslandsActions from '../../actions/FeaturedIslandsActions';
import CodeNotFound from '../../components/creativeMode/islands/CodeNotFound';
import HeaderSection from '../../components/creativeMode/islands/HeaderSection';
import SocialShareBanner from '../../components/creativeMode/islands/SocialShareBanner';
import Steps from '../../components/creativeMode/islands/Steps';
import {getImage} from '../../components/creativeMode/islands/utils';
import {FeaturedIslandsStore, LoginStore} from '../../stores';
import root from 'window-or-global';
import epicTracking from 'epic-tracking';

import {getIslandPageMetaTags} from './Utils';

const slug = '/island';

interface IslandViewPageData extends PageData {
  headerImg?: string;
  headerImg2?: string;
  arrowDescription?: string;
  stepsHeader?: string;
  stepsContent: {
    _type?: string;
    stepsContent: {
      bgImg?: string;
      showCode?: boolean;
      stepNumber?: string;
      title1: string;
      title2: string;
      _type: string;
    }[];
  };
  titleTemplate: string;
  descriptionTemplate: string;
  ogTitleTemplate: string;
  twitterSite: string;
  addFavorites?: string;
  addedFavoritesNotice?: string;
  favoritesAdded?: string;
  favoritesSeeExample?: string;
}

const StyledCreativeContentWrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding-block: 64px;
`;

const CreativeIslandView = (props) => {
  const {pageData, isLoading, error: cmsError} = useCmsData<IslandViewPageData>(slug);

  const {loginStore, featuredIslandsStore, location, match, localizationStore} = props;

  const {code: codeWithTitle} = match.params;
  const islandCode = islandUrlStripTitle(codeWithTitle);

  const addToFavorites = async () => {
    const {isLoggedIn} = loginStore;
    const {pathname} = location;

    if (!isLoggedIn) {
      let queryParams = {};
      if (location.search) {
        queryParams = qs.parse(location.search);
      }

      const params = {
        isCta: true,
        ...queryParams,
      };

      const redirectUrl = `${pathname}?${qs.stringify(params)}`;
      LoginActions.requestLoginModal({redirectUrl});
    } else {
      const {code: codeWithTitle} = match.params;
      const islandCode = islandUrlStripTitle(codeWithTitle);
      try {
        FeaturedIslandsActions.addFavorites(islandCode);

        if (root._epicTracking) {
          epicTracking.trackEvent('Interaction', {
            eventCategory: 'add_to',
            eventAction: 'cta',
            eventLabel: 'favorites_Creative_island',
            eventValue: 'TRUE',
            interactionType: 'click',
          });
        }
      } catch (error) {
        console.error('Failed to add island to favorites');
      }
    }
  };

  const addToPlaylist = async () => {
    const {isLoggedIn} = loginStore;
    const {pathname} = location;

    let queryParams = {};
    if (location.search) {
      queryParams = qs.parse(location.search);
    }

    if (!isLoggedIn) {
      const params = {
        isCta: true,
        ...queryParams,
      };

      const redirectUrl = `${pathname}?${qs.stringify(params)}`;
      LoginActions.requestLoginModal({redirectUrl});
    } else {
      const {code: codeWithTitle} = match.params;
      const islandCode = islandUrlStripTitle(codeWithTitle);
      try {
        FeaturedIslandsActions.addToPlaylist(islandCode);

        if (root._epicTracking) {
          epicTracking.trackEvent('Interaction', {
            eventCategory: 'add_to',
            eventAction: 'cta',
            eventLabel: 'playlist_Creative_island',
            eventValue: 'TRUE',
            interactionType: 'click',
          });
        }
      } catch (error) {
        console.error('Failed to add island to playlist');
      }
    }
  };

  const removeFromPlaylist = async () => {
    const {isLoggedIn} = loginStore;
    const {pathname} = location;

    let queryParams = {};
    if (location.search) {
      queryParams = qs.parse(location.search);
    }

    if (!isLoggedIn) {
      const params = {
        isCta: true,
        ...queryParams,
      };

      const redirectUrl = `${pathname}?${qs.stringify(params)}`;
      LoginActions.requestLoginModal({redirectUrl});
    } else {
      const {code: codeWithTitle} = match.params;
      const islandCode = islandUrlStripTitle(codeWithTitle);
      try {
        FeaturedIslandsActions.removeFromPlaylist(islandCode);

        if (root._epicTracking) {
          epicTracking.trackEvent('Interaction', {
            eventCategory: 'remove_from',
            eventAction: 'cta',
            eventLabel: 'playlist_Creative_island',
            eventValue: 'TRUE',
            interactionType: 'click',
          });
        }
      } catch (error) {
        console.error('Failed to remove island from playlist');
      }
    }
  };

  const {
    favoriteIslandsCodes,
    playlistIslandsCodes,
    island,
    loading: featuredIslandsLoading,
    error,
    addFavoriteCodeLoading,
    addFavoriteCodeError,
  } = featuredIslandsStore;

  useEffect(() => {
    const {isLoggedIn} = loginStore;
    if (isLoggedIn) {
      FeaturedIslandsActions.checkPlaylist(island?.code);
    }
  }, [island]);

  if (isLoading || Object.keys(pageData).length === 0 || featuredIslandsLoading) {
    return <LoadScreen show />;
  }

  if (error || cmsError) {
    return <HttpErrorScreen statusCode={error.response?.status ?? 500} />;
  }

  if (!island || !island?.success) {
    return <CodeNotFound />;
  }

  const {
    headerImg,
    headerImg2,
    arrowDescription,
    stepsHeader,
    stepsContent: {stepsContent},
    titleTemplate,
    descriptionTemplate,
    ogTitleTemplate,
    twitterSite,
    addFavorites,
    addedFavoritesNotice,
    favoritesAdded,
    favoritesSeeExample,
  } = pageData;

  const {displayName, data, active, owner, moderationStatus} = island;

  let codeFavored = false;
  if (Array.isArray(favoriteIslandsCodes) && favoriteIslandsCodes.length > 0) {
    codeFavored = favoriteIslandsCodes.includes(islandCode);
  }

  let isAddedToPlaylist = false;
  if (Array.isArray(playlistIslandsCodes) && playlistIslandsCodes.length > 0) {
    isAddedToPlaylist = playlistIslandsCodes.includes(islandCode);
  }

  // When hideUGC is true hide user generated content
  const hideUGC = moderationStatus !== 'Approved';

  const image = getImage(data, headerImg, headerImg2);
  // Retrieve defaultImage.
  const defaultImage = getImage({islandType: data && data.islandType}, headerImg, headerImg2);
  const imageSsl = image && image.replace(/^http:/, 'https:');
  const {locale} = localizationStore;
  const tags = getIslandPageMetaTags(
    localizationStore,
    imageSsl,
    titleTemplate,
    descriptionTemplate,
    ogTitleTemplate,
    data.title,
    data.tagline,
    islandCode,
    displayName,
    twitterSite,
    locale
  );

  // Unmoderated content will not be tracked by engines.
  const tagsOverride = {
    meta: [{name: 'robots', content: 'noindex,nofollow'}],
  };

  return (
    <div id="creative-mode" className="CreativeIslandView">
      {!hideUGC ? <Helmet {...tags} /> : <Helmet {...tagsOverride} />}

      {!hideUGC ? (
        <SocialShareBanner
          shareConfigs={['twitter', 'facebook', 'reddit']}
          meta={tags.meta}
          disableShareIcons={false}
          locale={props.localizationStore.locale}
        />
      ) : null}

      <div>
        <HeaderSection
          imgUrl={hideUGC ? defaultImage : imageSsl}
          owner={owner}
          creator={displayName}
          title={!hideUGC ? data.title : ''}
          tagline={!hideUGC ? data.tagline : ''}
          youtubeVideoId={data.youtube_video_id}
          code={islandCode}
          arrowDescription={arrowDescription}
          addToFavorites={addToFavorites}
          addToPlaylist={addToPlaylist}
          removeFromPlaylist={removeFromPlaylist}
          loading={featuredIslandsLoading}
          codeFavored={codeFavored}
          isAddedToPlaylist={isAddedToPlaylist}
          addFavoriteCodeLoading={addFavoriteCodeLoading}
          addFavoriteCodeError={addFavoriteCodeError}
          active={active}
          error={error}
          resetError={() => FeaturedIslandsActions.resetError()}
          addFavorites={addFavorites}
          addedFavoritesNotice={addedFavoritesNotice}
          favoritesAdded={favoritesAdded}
          favoritesSeeExample={favoritesSeeExample}
          hideUGC={hideUGC}
        />
      </div>
      <Container maxWidth={1920} size="lg">
        <StyledCreativeContentWrapper>
          <Steps stepsHeader={stepsHeader} steps={stepsContent} />
        </StyledCreativeContentWrapper>
      </Container>
    </div>
  );
};

CreativeIslandView.propTypes = {
  featuredIslandsStore: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  loginStore: PropTypes.object,
  localizationStore: PropTypes.object,
  creativeModeStore: PropTypes.object,
  match: PropTypes.object,
};

CreativeIslandView.getStores = () => [LoginStore, LocalizationStore, FeaturedIslandsStore];

CreativeIslandView.getPropsFromStores = () => ({
  loginStore: LoginStore.getState(),
  localizationStore: LocalizationStore.getState(),

  featuredIslandsStore: FeaturedIslandsStore.getState(),
});

CreativeIslandView.getInitialSharedValues = getInitialCmsPageSharedValues(slug);

export default connectToStores(withRouter(CreativeIslandView));
